/**
 * PostHog ingest proxy — the Cloudflare Pages half of `plugins/posthog.ts`.
 *
 * `plugins/posthog.ts` sets `api_host: '/ingest'`, a RELATIVE path, so the
 * browser only ever talks to our own origin and every app keeps
 * `connect-src 'self'` (Ahmed, 2026-09-10, decision 2). Something first-party
 * has to answer that path, and which something depends on where the app runs:
 *
 *   - VPS apps (portal, apply, demo, vantage) — Caddy, `handle_path /ingest/*`
 *     in infrastructure/caddy-config/Caddyfile.
 *   - Cloudflare Pages apps (broker at network.claimshieldpro.com; driver at
 *     drivers.claimshieldpro.com, mechanics.claimshieldpro.com, and legacy
 *     rewards.claimshieldpro.com) — a Pages Function per project, which is what
 *     this file implements. **There is no Caddy in front of a Pages project**,
 *     so the Caddyfile stanza does not reach them at all.
 *
 * WHY THIS LIVES IN `shared` AND NOT IN THE TWO `functions/` DIRECTORIES.
 * A Pages Function is not covered by either package's `tsconfig.json` — both
 * `include` only `src/**`, so `vue-tsc` never sees `functions/**` and a mistake
 * there surfaces at deploy or at runtime, not in `pnpm type-check`. Putting the
 * logic here instead means it is type-checked, and unit-tested by
 * `pnpm --filter @claimshield/shared test` — which is the gate
 * .github/workflows/deploy-driver-portal.yml runs before it publishes anything.
 * It also means one implementation rather than two copies drifting apart, which
 * is the failure mode .claude/CLAUDE.md → "Sweeping a Change Across Sites"
 * exists to prevent.
 *
 * BEHAVIOUR IS DELIBERATELY IDENTICAL TO THE CADDY BLOCK, including the parts
 * that look like omissions:
 *
 *   - No caching. PostHog's own Cloudflare guide caches assets in
 *     `caches.default`. We do not, for two reasons: a cache hit still bills as
 *     a Function invocation so it buys no quota, and this repo has already been
 *     bitten by a cached edge object outliving the deploy that produced it
 *     (knowledge/engineering-guardrails/2026-08-13-cf-asset-poisoning-shared-blast-radius.md
 *     — the same reasoning that keeps `public/_headers` off long immutable
 *     TTLs). Caddy does not cache; neither does this.
 *   - Cookie and Authorization are stripped. Our session cookie and bearer
 *     token must never reach a vendor, and PostHog does not want them —
 *     ingestion authenticates on the project token in the request body.
 *   - `Access-Control-Allow-Origin` is stripped from the response. The call is
 *     same-origin, so CORS is not needed; leaving PostHog's header on would
 *     only create a chance of it conflicting with ours.
 */

/** Regional ingest host. US for this project — check the project URL before copying. */
export const POSTHOG_API_HOST = 'us.i.posthog.com'

/** Regional static-asset host. Serves the SDK bundle and per-token remote config. */
export const POSTHOG_ASSET_HOST = 'us-assets.i.posthog.com'

/** The path prefix the SDK is pointed at. Must match INGEST_PATH in plugins/posthog.ts. */
export const INGEST_PREFIX = '/ingest'

/**
 * Which upstream a request belongs to, and the URL to send it to.
 *
 * Pure, so the routing rule — the part that is actually easy to get wrong — is
 * testable without a network, a Worker runtime or a browser.
 *
 * The split mirrors Caddy's three `handle` blocks exactly: `/static/*` and
 * `/array/*` are the asset host, everything else (event capture, feature flags,
 * remote config) is the ingest host. Phase 1 proved the split is load-bearing
 * rather than decorative: a capture POST sent to the ASSET host 404s
 * (TRACK.md §7.4), so getting this backwards fails closed but silently.
 */
export function resolvePosthogUpstream(
  pathname: string,
  search = '',
): { url: string; host: string; isAsset: boolean } {
  // Strip the prefix, exactly as Caddy's `handle_path` does. Everything
  // downstream is written against PostHog's own path shapes.
  const path = pathname.startsWith(INGEST_PREFIX)
    ? pathname.slice(INGEST_PREFIX.length) || '/'
    : pathname

  const isAsset = path.startsWith('/static/') || path.startsWith('/array/')
  const host = isAsset ? POSTHOG_ASSET_HOST : POSTHOG_API_HOST

  return { url: `https://${host}${path}${search}`, host, isAsset }
}

/**
 * Headers to send upstream: the caller's, minus anything that is ours to keep.
 *
 * `Host` is removed rather than rewritten. Caddy has to set it explicitly
 * (`header_up Host …`) because PostHog routes on it and answers 401 when it is
 * wrong — that 401 is Phase 1's canary. In a Worker the runtime derives `Host`
 * from the request URL, so removing the inbound one and letting it be
 * regenerated produces the same result by a different route.
 *
 * `X-Forwarded-For` carries the real client IP from Cloudflare's
 * `CF-Connecting-IP`. Without it PostHog geolocates every event to a Cloudflare
 * data centre. Caddy's `reverse_proxy` appends XFF on its own, so this is
 * parity with the VPS path, not a new behaviour.
 */
export function buildUpstreamHeaders(incoming: Headers): Headers {
  const headers = new Headers(incoming)
  headers.delete('cookie')
  headers.delete('authorization')
  headers.delete('host')

  const clientIp = incoming.get('CF-Connecting-IP')
  if (clientIp) headers.set('X-Forwarded-For', clientIp)

  return headers
}

/**
 * Proxy one request to PostHog. This is the whole Pages Function.
 *
 * The body is read with `arrayBuffer()` rather than streamed. PostHog's
 * Cloudflare guide calls this out by name: forwarding `request.body` as a
 * `ReadableStream` drops encoded POST payloads, so page views arrive and
 * `capture()` events silently do not — which reads exactly like a broken
 * install rather than a broken proxy.
 */
export async function proxyPosthogRequest(request: Request): Promise<Response> {
  const url = new URL(request.url)
  const { url: target } = resolvePosthogUpstream(url.pathname, url.search)

  const hasBody = request.method !== 'GET' && request.method !== 'HEAD'

  const upstream = await fetch(target, {
    method: request.method,
    headers: buildUpstreamHeaders(request.headers),
    body: hasBody ? await request.clone().arrayBuffer() : undefined,
    redirect: 'manual',
  })

  // fetch() responses have immutable headers, so rebuild to drop PostHog's CORS
  // header. Same-origin needs none.
  const headers = new Headers(upstream.headers)
  headers.delete('access-control-allow-origin')

  return new Response(upstream.body, {
    status: upstream.status,
    statusText: upstream.statusText,
    headers,
  })
}
